import React, { useEffect, useMemo } from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import { getEnv } from 'mobx-state-tree';
import CircularProgress from '@mui/material/CircularProgress';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { Grid } from '@shared/components/Grid';
import { SearchForm } from '@shared/components/SearchForm';
import { NoResultsView } from '@shared/components/NoResultsView';
import { Button } from '@shared/components/Button';
import { useAnalyticsGridUIStore } from '@core/useStores';
import { IStoresEnv } from '@core/storesEnv';
import { RoutePaths } from '@core/routes/RoutePaths';

import { NoUsersInGroup } from '../NoUsersInGroup';
import { GridItem } from '../AnalyticsPage/GridItem';
import { messages } from 'Analytics/analytic.messages';
import { useStyles } from '../AnalyticsPage/AnalyticsPage.styles';
import { ThemeProps } from '@styles/theme';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { IUserAnalyticStore } from 'Analytics/stores';
import { AllExecutionsExport } from '../AllExecutionsExport';

type FetchDataType = {
    pageIndex: number;
};

const initialSortState = {
    sorting: [{
        id: 'fullName',
        desc: false,
    }],
};

const columnHelper = createColumnHelper<IUserAnalyticStore>();

const columnsData = [
    columnHelper.accessor('userId', {}),
    columnHelper.accessor('userFirstName', {}),
    columnHelper.accessor('userLastName', {}),
    columnHelper.accessor('completedProcedures', {}),
    columnHelper.accessor('assignedProcedures', {}),
];

export const LegacyAnalyticsPage: React.FC = () => {
    const analyticsGridUIStore = useAnalyticsGridUIStore();
    const { filtersOptions } = getEnv<IStoresEnv>(analyticsGridUIStore);
    const navigate = useNavigate();

    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const columns: ColumnDef<IUserAnalyticStore, any>[] = useMemo(() => columnsData, []);

    useEffect(() => {
        analyticsGridUIStore.togglePageActivity(true);
        analyticsGridUIStore.load();
        analyticsGridUIStore.loadStatistics();
        filtersOptions.loadFilterOptions();

        return () => {
            analyticsGridUIStore.togglePageActivity(false);
            analyticsGridUIStore.cleanUp();
        };
    }, []);

    const onFetchData = ({ pageIndex }: FetchDataType) => {
        analyticsGridUIStore.setParams({ page: pageIndex });
    };

    const onSearchTextChanged = (searchText: string) => {
        if (searchText && searchText.length === 1) {
            return;
        }

        analyticsGridUIStore.setParams({
            page: 0,
            searchText,
        });
    };

    return (
        <div className={styles.pageShell}>
            <div className={styles.legacySection}>
                <div className={styles.legacyHeader}>
                    <div className={styles.legacyHeaderText}>
                        <div className={styles.legacyEyebrow}>Legacy Analytics</div>
                        <div className={styles.legacyTitle}>Completion-only analytics</div>
                        <div className={styles.legacyDescription}>This page preserves the original analytics experience. Use Neo Analytics for execution-level insights and richer operational filtering.</div>
                    </div>
                    <Button className={styles.legacyToggle} onClick={() => navigate(`/${RoutePaths.analytics}`)} color="secondary">
                        Back To Neo Analytics
                    </Button>
                </div>
                <div className={styles.legacyContent}>
                    <div className={styles.gridActions}>
                        <Observer>
                            {() => (
                                <>
                                    <div className={styles.pageTitle}>
                                        <Typography variant="h6">{messages['analyticsPage.pageTitle']}</Typography>
                                        {analyticsGridUIStore.status.isLoading && <CircularProgress className={styles.loader} size={24} />}
                                    </div>
                                    <div className={styles.filters}>
                                        <SearchForm
                                            initialValues={{
                                                search: analyticsGridUIStore.requestParams.getStorageParams
                                                    ? analyticsGridUIStore.requestParams.getStorageParams.searchText
                                                    : '',
                                            }}
                                            onSearchTextChanged={onSearchTextChanged}
                                            className={styles.searchWrapper}
                                        />
                                        <AllExecutionsExport />
                                    </div>
                                </>
                            )}
                        </Observer>
                    </div>
                    <Observer>
                        {() => {
                            const data = analyticsGridUIStore.users.toJSON();

                            return (
                                <>
                                    {analyticsGridUIStore.isTableVisible && (
                                        <Grid
                                            data={data}
                                            columns={columns}
                                            fetchData={onFetchData}
                                            GridItem={GridItem}
                                            isLoading={analyticsGridUIStore.status?.isLoading}
                                            paginationStatus={{ ...analyticsGridUIStore.pagination }}
                                            initialState={initialSortState}
                                        />
                                    )}
                                    {analyticsGridUIStore.noFilteringResults && <NoResultsView entityName='users' />}
                                    {analyticsGridUIStore.noItemsInGroup && <NoUsersInGroup />}
                                </>
                            );
                        }}
                    </Observer>
                </div>
            </div>
        </div>
    );
};